// Composer Studio — 미리보기 팝아웃 창 BroadcastChannel 컨트랙트.
//
// 부모(EditorPage)와 팝업(PreviewPopoutPage)이 이 모듈을 함께 import해 채널 이름/메시지 shape를
// 단일 정의로 공유한다(문자열 오타·shape drift 방지). 동시 1팝업 전제의 단일 채널.
//
// 핸드셰이크: 팝업 mount 시 `ready` 방송 → 부모가 현재 {template, content}로 `sync` 1회 응답 →
// 이후 content/template 변경마다 `sync`(작성폼 입력은 debounce). 부모 언마운트/탭 이탈 시 `closed`.

import type { ReportTemplate } from '@safetyhub/report-composer';
import type { DetailChromeConfig } from '@safetyhub/shared';
import type { PreviewProvenanceMaterial } from './previewLinkedReport';

/** 단일 팝업 전제의 고정 채널명. */
export const PREVIEW_POPOUT_CHANNEL = 'composer:preview-popout';

/** 팝업 창을 여는 라우트(App.tsx에 PrivateRoute로 등록). */
export const PREVIEW_POPOUT_ROUTE = '/report-composer/preview-popout';

/** window.open의 target 이름 — 재클릭 시 같은 창을 focus(중복 창 방지). */
export const PREVIEW_POPOUT_WINDOW_NAME = 'composer-preview-popout';

/**
 * 부모→팝업 sync 페이로드.
 * - `content`: 미리보기 resolve content(compose 스냅샷 = select 경로 키). 팝업이 자체적으로
 *   `mergePreviewContent(template, content)`로 RenderData를 산출한다(서버 왕복 없음).
 * - `hasRealData`: previewResult(실데이터 반영) 여부. 팝업의 bindingPlaceholders 모드 구동
 *   (실데이터면 'unresolvable-only' — 미해소만 amber, 아니면 true — 전체 회색) —
 *   미리보기 탭의 `bindingPlaceholders={previewResult ? 'unresolvable-only' : true}` 정책과 동일하게 맞춘다.
 */
export interface PreviewSyncPayload {
  template: ReportTemplate;
  content: Record<string, unknown>;
  hasRealData: boolean;
  /**
   * 부모 편집 뷰 모드 — 팝업이 보고서 본문('form') vs 작성 페이지('writeForm')를 구분해 렌더.
   *
   * ⚠️ 이 창은 **보고서(양식) 전용**이다(2026-07-22 사용자 확정) — `writeForm`이면 팝업은 작성폼을
   * 그리지 않고 **안내 화면만** 표시한다(`PreviewPopoutPage` 헤더·`viewMode === 'writeForm'` 분기).
   * 작성 페이지 미리보기는 캔버스의 "비교" 보기가 상위 호환이다. 종전 이 자리엔 「팝업이
   * composerWidgetTree(없으면 deriveComposerWidgets 파생)를 작성폼으로 렌더한다」고 적혀 있었는데
   * 그 서술은 위 결정 전의 것이라 **오래 거짓**이었다(2026-09-30 S3-b 작성 폼 갈래 정리 중 발견) —
   * 작성폼 렌더 폴백 갈래가 여기 있는 것으로 오독하지 말 것.
   */
  viewMode: 'form' | 'writeForm';
  /**
   * 결재 서명 단계 모사 값(`__approvalSigned` 축, 2026-09-04) — 서버 resolve content에 실리지
   * 않는(서버가 명시적으로 건너뛰는) 클라 전용 축이라, content와 별도로 실어 팝업의
   * `applySimulatedApproval`이 부모 미리보기와 같은 표본 서명을 그리게 한다. 미지정=빈 결재란.
   */
  approvalSignedLevel?: number;
  /**
   * 값 출처 표시 재료(모사 축 키 / 연결 보고서 / reportScoped 키) — 팝업이 부모와 같은 밑줄을
   * 그리게 한다. **배열 형상**인 이유는 채널이 구조적 복제라 `Set`을 그대로 못 건네기 때문이고
   * (팝업이 `toBindingProvenance`로 Set을 만든다), 카탈로그 전체 대신 reportScoped 키만 싣는
   * 이유는 sync가 편집 keystroke마다 도는 채널이기 때문이다. 미지정=실데이터 미반영(표시 없음).
   */
  provenance?: PreviewProvenanceMaterial;
}

/** 채널 위를 오가는 메시지 유니온. */
export type PreviewPopoutMessage =
  | { type: 'ready' }
  | ({ type: 'sync' } & PreviewSyncPayload)
  // 부모→팝업: 미배치 풀 위젯 hover 시 그 바인딩이 놓인 body 노드로 스크롤+하이라이트.
  // nodeId=null이면 하이라이트 해제. cell은 표 셀 좌표(향후용 — 현재 팝업은 표 노드 단위 하이라이트).
  | { type: 'highlight-node'; nodeId: string | null; cell?: { row: number; col: number } }
  // 부모→팝업: 상세 chrome(헤더/컨트롤 바) 편집 모드 켜기(F8 편집 버튼 클릭 시).
  | { type: 'enterChromeEdit' }
  // 팝업→부모: chrome 속성 편집 결과. undefined=구성 제거(코드 기본 배치로 복귀).
  | { type: 'detailChromeEdit'; detailChrome: DetailChromeConfig | undefined }
  // 팝업→부모: 저장 요청(팝업 저장 버튼/Ctrl+S). 부모가 handleSave 실행(busy면 무시).
  | { type: 'save' }
  | { type: 'closed' };
